<template>
  <v-container fluid class="hyp-table-container pa-0">
    <v-row dense>
      <v-col class="pb-0 ma-0">
        <v-dialog
          v-model="dialog"
          max-width="1000px"
          content-class="asset-dialog"
          scrollable
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              outlined
              small
              v-bind="attrs"
              v-on="on"
              v-on:click="createAsset"
              v-show="hasAssetsMgmtAdminAccess && !selecting"
              class="pull-left mr-5"
            >
              {{ labelAddNew }}
            </v-btn>
          </template>
          <v-card>
            <v-card-title>
              <v-row>
                <v-col>
                  <span>{{ formTitle }}</span>
                </v-col>
                <v-col cols="1">
                  <v-icon right v-on:click="close">mdi-close</v-icon>
                </v-col>
              </v-row>
            </v-card-title>

            <v-card-text style="height: 650px;">
              <v-container class="pt-0">
                <v-form ref="form" v-model="validForm">
                  <v-row>
                    <v-col cols="6" class="pt-0">
                      <v-text-field
                        v-model="asset.title"
                        :counter="100"
                        :label="labelName"
                        :rules="nameRules"
                        required
                      ></v-text-field>
                      <v-menu
                        ref="menu"
                        v-model="menu"
                        :close-on-content-click="false"
                        :return-value.sync="asset.expirationDate"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="expirationDateFormatted"
                            label="Expiration Date"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="asset.expirationDate"
                          no-title
                          scrollable
                          :show-current="utcDate"
                          :min="utcDate"
                        >
                          <v-spacer></v-spacer>
                          <v-btn text color="primary" @click="menu = false">
                            Cancel
                          </v-btn>
                          <v-btn
                            text
                            color="primary"
                            @click="$refs.menu.save(asset.expirationDate)"
                          >
                            OK
                          </v-btn>
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="6" class="pt-0">
                      <v-select
                        v-model="asset.assetTypeId"
                        :items="sortedAssetTypes"
                        item-text="title"
                        item-value="id"
                        :label="labelType"
                        :rules="requiredRules"
                      ></v-select>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-divider class="py-2"></v-divider>
                  </v-row>

                  <v-row>
                    <v-col cols="12" class="pt-0">
                      <AssetsEditor
                        :asset="asset"
                        @change="handleOnChange"
                      ></AssetsEditor>
                    </v-col>
                  </v-row>

                  <v-row class="location-textfields">
                    <v-col cols="6">
                      <v-text-field
                        v-model="asset.fullAddress"
                        type="text"
                        label="Address"
                        disabled
                      ></v-text-field>
                      <v-text-field
                        v-model.number="asset.latitude"
                        type="number"
                        step="any"
                        :rules="latitudeRules"
                        label="Latitude"
                        disabled
                        required
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        v-model="asset.country.name"
                        type="text"
                        label="Country"
                        disabled
                      ></v-text-field>
                      <v-text-field
                        v-model.number="asset.longitude"
                        type="number"
                        step="any"
                        :rules="longitudeRules"
                        label="Longitude"
                        required
                        disabled
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row> <v-divider class="py-2"></v-divider> </v-row>
                  <!-- Dynamically display custom fields -->
                  <v-row>
                    <v-col
                      cols="6"
                      v-for="(field, index) in sortedEnabledCustomFields"
                      v-bind:key="index"
                    >
                      <v-textarea
                        :label="field.title"
                        :rows="1"
                        auto-grow
                        :counter="750"
                        v-model="asset.customFields[field.title]"
                        @input="
                          updateCustomField(
                            asset.customFields,
                            `${field.title}`,
                            $event
                          )
                        "
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </v-form>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text class="default btn mr-5" @click="close">
                Cancel
              </v-btn>
              <v-btn
                class="primary btn mr-3"
                @click="saveAsset"
                :disabled="!validForm || saving"
              >
                Save
                <v-progress-circular
                  v-if="saving"
                  indeterminate
                  size="14"
                  width="2"
                  class="ml-2"
                ></v-progress-circular>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card>
            <v-card-title>
              Are you sure you want to delete {{ asset.title }}?
            </v-card-title>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn class="default" text @click="closeDelete">Cancel</v-btn>
              <v-btn
                :disabled="deleting"
                class="primary"
                text
                @click="deleteAssetConfirm"
              >
                Delete
                <v-progress-circular
                  v-if="deleting"
                  indeterminate
                  size="14"
                  width="2"
                  class="ml-2"
                ></v-progress-circular>
              </v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-btn
          v-if="isAssetsMgmtAlertsFeatureUnlocked && !selecting"
          color="primary"
          small
          outlined
          class="mb-2 mr-5 pull-left"
          v-on:click="handleClickAlert"
          >Create Asset Alert</v-btn
        >

        <v-switch
          v-if="!selecting"
          v-model="assetsEnabled"
          solo
          class="pull-left pa-0 ma-0"
          label="View on map"
          hide-details
          v-on:change="handleChangeAssetsEnabled"
        ></v-switch>

        <div class="cleafix"></div>
      </v-col>
      <v-col cols="7">
        <v-spacer></v-spacer>
      </v-col>
    </v-row>

    <v-row dense class="pa-0 ma-0">
      <v-col cols="4" class="mt-1">
        <LocationsSelector
          :selected-locations="selectedCountries"
          :show-regions="false"
          label-prefix="Filter"
          @change-locations="handleChangeLocations"
        ></LocationsSelector>
      </v-col>
      <v-col cols="4">
        <v-text-field
          v-model="options.filterTitle"
          append-icon="mdi-magnify"
          :label="labelFilterTitle"
          single-line
          hide-details
          style="width: 450px;"
          clearable
          class="px-3"
        >
        </v-text-field>
      </v-col>
      <v-col cols="4">
        <v-select
          v-model="options.filterAssetTypeIds"
          :items="assetTypes"
          item-text="title"
          item-value="id"
          :label="labelFilterAssetTypes"
          hide-details
          multiple
          clearable
        >
          <template v-slot:selection="{ index }">
            <span v-if="index === 0">
              {{ options.filterAssetTypeIds.length }} selected
            </span>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <v-row>
      <v-col class="hyp-data-table-container">
        <v-data-table
          v-model="selected"
          dense
          v-resize="handleResize"
          :headers="headers"
          :items="assetItems"
          :items-per-page="itemsPerPage"
          :server-items-length="totalAssets"
          sort-by="title"
          :height="listingHeight"
          fixed-header
          :loading="loading"
          :loading-text="labelLoading"
          class="assets-data-table"
          :options.sync="options"
          @update:options="handleTableOptions"
          :footer-props="footerProps"
          :show-select="!!selecting"
          @item-selected="handleItemSelected"
          @toggle-select-all="handleToggleSelectAll"
        >
          <template v-slot:item.action="{ item }">
            <v-icon small class="mr-2" @click="flyToItem(item)">
              mdi-earth
            </v-icon>
            <v-icon
              small
              class="mr-2"
              @click="editAsset(item)"
              v-show="hasAssetsMgmtAdminAccess && !selecting"
            >
              mdi-pencil
            </v-icon>
            <v-icon
              small
              @click="removeAsset(item)"
              v-show="hasAssetsMgmtAdminAccess && !selecting"
            >
              mdi-delete
            </v-icon>
          </template>
          <template v-slot:item.country.name="{ item }">
            <div v-if="item.country && item.country.name">
              {{ item.country.name }}
            </div>
          </template>
          <template v-slot:item.assetType.title="{ item }">
            <div
              v-if="
                item.assetType && item.assetType.icon && item.assetType.color
              "
            >
              <AssetIcon
                :icon-size="14"
                :container-size="25"
                :icon="item.assetType.icon"
                :color="item.assetType.color"
              />
              <div class="asset-type-txt">{{ item.assetType.title }}</div>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="my-5">
              There are currently no assets. <br /><br />
              <div v-show="hasAssetsMgmtAdminAccess">
                You can create a new asset by clicking the <br />
                Create New Asset button above.
              </div>
            </div>
          </template>
          <template v-slot:no-results>
            <div class="my-5">
              Your search returned no results.
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashForEach from "lodash/forEach";
import lodashRemove from "lodash/remove";

import { mapGetters } from "vuex";
import { ASSET_CUSTOM_AOI } from "@/common/constants/assetManagement";
import {
  NOTIFICATION_ASSET_TYPE_VERIFIED_EVENT,
  NOTIFICATION_FREQUENCY_IMMEDIATELY,
  NOTIFICATION_EXPIRATION_ID_NEVER,
} from "@/common/constants/notifications";
import { LABELS } from "@/common/constants/labels";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import { getFormattedDate } from "@/common/date.utilities";
import {
  MODULE_PREFIX_TIME,
  MODULE_PREFIX_ASSETS,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_NOTIFICATIONS,
  MODULE_PREFIX_MAP,
} from "@/store";
import {
  SAVE_PREFERENCES,
  GET_ASSETS_REFRESH_MAP,
  GET_ASSETS,
  GET_ASSET_TYPES,
  GET_ASSET_CUSTOM_FIELDS,
  SAVE_ASSET,
  DELETE_ASSET,
  GET_ASSETS_SELECTED,
  GET_NOTIFICATION,
  GET_MODAL,
  GET_ASSETS_TABLE_OPTIONS,
} from "@/store/actions.type";
import {
  getAllCountries,
  getCityFromGooglePlace,
  getCountryFromGooglePlace,
} from "@/common/maps/utilities";
import { hasAssetsMgmtAdminAccess } from "@/services/user/user.service";

const sortOptions = {
  NONE: "",
  ASCENDING: "ASC",
  DESCENDING: "DESC",
};

const headerOptionValues = {
  COUNTRY_NAME: "country.name",
  TITLE: "title",
  ASSET_TYPE: "assetType.title",
};

const EMITTED_ACTIONS = {
  CHANGE_FILTER: "change-filter",
};

export default {
  name: "AssetsList",
  components: {
    AssetsEditor: () => import("@/components/assetManagement/AssetsEditor"),
    AssetIcon: () => import("@/components/assetManagement/AssetIcon"),
    LocationsSelector: () => import("@/components/common/LocationsSelector"),
  },
  props: {
    selecting: {
      type: String,
    },
    filtersSelected: {
      type: Object,
    },
    alertId: {
      type: String,
    },
  },
  data: function () {
    return {
      validForm: false,
      selected: [],
      selectAll: false,
      actionsHeader: [
        { text: "Actions", value: "action", sortable: false, width: 100 },
      ],
      initialHeaders: [
        {
          text: "Country",
          value: headerOptionValues.COUNTRY_NAME,
          sortable: true,
          width: 250,
        },
        {
          text: "Title",
          value: headerOptionValues.TITLE,
          sortable: true,
          width: 250,
        },
        {
          text: "Type",
          value: headerOptionValues.ASSET_TYPE,
          sortable: true,
          width: 250,
        },
      ],
      finalHeaders: [
        { text: "Latitude", value: "latitude", sortable: false },
        { text: "Longitude", value: "longitude", sortable: false },
      ],
      labelFilterTitle: `Search My ${LABELS.assets}`,
      labelFilterAssetTypes: "Filter Asset Types",
      labelFilterCountries: "Filter Countries",
      labelAddNew: `Add New ${LABELS.asset}`,
      labelLoading: `Retrieving ${LABELS.assets}`,
      labelName: `${LABELS.asset} name`,
      labelType: `${LABELS.asset} type`,
      loading: false,
      countries: null,
      inputTextCountries: "",
      windowSize: {
        x: 0,
        y: 0,
      },
      dialog: false,
      dialogDelete: false,
      deleting: false,
      saving: false,
      editedIndex: -1,
      emptyAsset: {
        title: "",
        assetType: {
          color: "",
          icon: "",
          id: null,
          name: "",
        },
        latitude: 0,
        longitude: 0,
        fullAddress: null,
        country: {
          name: "",
          code: "",
        },
        city: "",
        googlePlace: null,
        isSystemType: false,
        enabled: false,
        customFields: {},
        expirationDate: null,
      },
      asset: {
        title: "",
        assetType: {
          color: "",
          icon: "",
          id: null,
          name: "",
        },
        latitude: 0,
        longitude: 0,
        fullAddress: null,
        country: {
          name: "",
          code: "",
        },
        city: "",
        googlePlace: null,
        isSystemType: false,
        enabled: false,
        customFields: {},
        expirationDate: null,
      },
      nameRules: [
        (value) => !!value || "Required.",
        (value) => (value || "").length <= 100 || "Max 100 characters",
      ],
      latitudeRules: [
        (value) => !!value || "Required.",
        (value) => value >= -90 || "Greater than -90",
        (value) => value <= 90 || "Less than 90",
      ],
      longitudeRules: [
        (value) => !!value || "Required.",
        (value) => value >= -180 || "Greater than -180",
        (value) => value <= 180 || "Less than 180",
      ],
      requiredRules: [(value) => !!value || "Required."],
      assetsEnabled: false,
      aoiFeature: ASSET_CUSTOM_AOI,
      menu: false,
      hasAssetsMgmtAdminAccess: hasAssetsMgmtAdminAccess(),
      isFormVisible: false,
      options: {
        filterTitle: null,
        filterCountries: [],
        filterAssetTypeIds: [],
        filterAlertId: null,
        titleSortDirection: sortOptions.NONE,
        countrySortDirection: sortOptions.NONE,
        assetTypeSortDirection: sortOptions.NONE,
        alertIdSortDirection: sortOptions.NONE,
      },
      pageNum: 1,
      itemsPerPage: 50,
      totalAssets: 0,
    };
  },
  async mounted() {
    await this.getAssets();
    await this.getAssetTypes();
    await this.getCustomFields();
    this.countries = await getAllCountries();
  },
  computed: {
    ...mapGetters({
      utcDate: MODULE_PREFIX_TIME + "today",
      preferences: MODULE_PREFIX_USER + "preferences",
      assets: MODULE_PREFIX_ASSETS + "assets",
      assetTypes: MODULE_PREFIX_ASSETS + "assetTypes",
      customFields: MODULE_PREFIX_ASSETS + "assetCustomFields",
      assetsSelected: MODULE_PREFIX_ASSETS + "assetsSelected",
      modal: MODULE_PREFIX_NOTIFICATIONS + "modal",
      notification: MODULE_PREFIX_NOTIFICATIONS + "notification",
      maps: MODULE_PREFIX_MAP + "maps",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    isAssetsMgmtAlertsFeatureUnlocked() {
      return this.featureUnlocked("assets-management-alerts");
    },
    listingHeight() {
      let height = this.windowSize.y - 350;
      if (this.selecting) {
        height -= 200;
      }
      return height;
    },
    sortedAssetTypes() {
      if (this.assetTypes) {
        return this.assetTypes.filter((assetType) => {
          return assetType.enabled;
        });
      }
      return null;
    },
    sortedEnabledCustomFields() {
      if (this.customFields) {
        return this.customFields.filter((field) => {
          return field.enabled;
        });
      }
      return null;
    },
    headers() {
      let headers = [];
      let customHeaders = [];
      lodashForEach(this.sortedEnabledCustomFields, (field) => {
        const title = field.title;
        customHeaders.push({
          text: title,
          value: "customFields." + title,
          sortable: true,
          width: 200,
        });
      });
      if (this.hasAssetsMgmtAdminAccess && !this.selecting) {
        headers.push(this.actionsHeader[0]);
      }
      return headers.concat(
        this.initialHeaders,
        ...customHeaders,
        this.finalHeaders
      );
    },
    formTitle() {
      return this.editedIndex === -1
        ? `New ${LABELS.asset}`
        : `Edit ${LABELS.asset}`;
    },
    expirationDateFormatted() {
      if (this.asset?.expirationDate) {
        return getFormattedDate(this.asset.expirationDate);
      }
      return null;
    },
    selectedCountries() {
      return { countries: this.options.filterCountries };
    },
    assetItems() {
      if (this.assets?.assets) {
        return this.assets.assets;
      }
      return [];
    },
    footerProps() {
      if (this.selecting) {
        return { "items-per-page-options": [1000] };
      }
      return { "items-per-page-options": [25, 50, 100, 500, 1000] };
    },
  },
  methods: {
    async getAssets() {
      await this.$store.dispatch(MODULE_PREFIX_ASSETS + GET_ASSETS, {
        options: this.options,
      });
      if (this.assets?.metadata) {
        this.pageNum = this.assets.metadata.pageNumber;
        this.itemsPerPage = this.assets.metadata.count;
        this.totalAssets = parseInt(this.assets.metadata.total);
      }
      if (this.alertId && this.assets?.assets) {
        const selectedAssets = [];
        this.assets.assets.forEach((item) => {
          if (item?.existsInAlert) {
            selectedAssets.push(item);
          }
        });
        this.$store.dispatch(
          MODULE_PREFIX_ASSETS + GET_ASSETS_SELECTED,
          selectedAssets
        );
      }
    },
    async getAssetTypes() {
      await this.$store.dispatch(MODULE_PREFIX_ASSETS + GET_ASSET_TYPES, {});
    },
    async getCustomFields() {
      await this.$store.dispatch(
        MODULE_PREFIX_ASSETS + GET_ASSET_CUSTOM_FIELDS,
        {}
      );
    },
    handleResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight };
    },
    handleChangeLocations(locations) {
      if (locations?.countries) {
        const countries = locations.countries.map((country) => {
          return country.value;
        });
        this.options.filterCountries = countries;
      }
    },
    createAsset() {
      this.reset();
      this.asset = this.emptyAsset;
      this.dialog = true;
    },
    editAsset(item) {
      this.editedIndex = this.assets.assets.indexOf(item);
      this.asset = item;
      this.asset.country = {
        name: item.country ? item.country.name : "",
        code: item.country.countryAlpha2Code
          ? item.country.countryAlpha2Code
          : "",
      };
      this.asset.customFields = item.customFields ? item.customFields : {};
      this.dialog = true;
    },
    removeAsset(item) {
      this.editedIndex = this.assets.assets.indexOf(item);
      this.asset = Object.assign({}, item);
      if (!this.asset.country) {
        this.asset.country = {
          name: "",
          code: "",
        };
      }
      this.dialogDelete = true;
    },
    async deleteAssetConfirm() {
      this.deleting = true;
      await this.$store.dispatch(
        MODULE_PREFIX_ASSETS + DELETE_ASSET,
        this.asset.id
      );
      this.closeDelete();
      this.deleting = false;
      this.$store.dispatch(
        MODULE_PREFIX_ASSETS + GET_ASSETS_REFRESH_MAP,
        new Date().toISOString()
      );
      await this.$store.dispatch(MODULE_PREFIX_ASSETS + GET_ASSETS, {
        options: this.options,
      });
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.reset();
        this.editedIndex = -1;
      });
    },
    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.reset();
        this.editedIndex = -1;
      });
    },
    async saveAsset() {
      if (this.$refs.form.validate()) {
        this.saving = true;
        await this.$store.dispatch(
          MODULE_PREFIX_ASSETS + SAVE_ASSET,
          this.asset
        );
        this.close();
        this.saving = false;
        this.$store.dispatch(
          MODULE_PREFIX_ASSETS + GET_ASSETS_REFRESH_MAP,
          new Date().toISOString()
        );
      } else {
        console.error("The form is not valid");
      }
    },
    handleOnChange(googlePlace) {
      this.asset.fullAddress = null;
      this.asset.country = null;
      this.asset.longitude = null;
      this.asset.latitude = null;
      this.asset.city = null;

      if (googlePlace) {
        if (googlePlace.formatted_address) {
          this.asset.fullAddress = googlePlace.formatted_address;
        }
        if (
          googlePlace.geometry?.location?.lng() &&
          googlePlace.geometry?.location?.lat()
        ) {
          this.asset.longitude = googlePlace.geometry.location.lng().toFixed(4);
          this.asset.latitude = googlePlace.geometry.location.lat().toFixed(4);
        }
        this.asset.country = getCountryFromGooglePlace(googlePlace);
        this.asset.city = getCityFromGooglePlace(googlePlace);
      }
      this.$refs.form.validate();
    },
    reset() {
      this.asset = this.emptyAsset;
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
    handleChangeAssetsEnabled() {
      this.$store.dispatch(
        MODULE_PREFIX_ASSETS + GET_ASSETS_REFRESH_MAP,
        new Date().toISOString()
      );

      if (this.assetsEnabled) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.assets,
          EVENT_ACTIONS.clicked,
          "Assets Enabled"
        );
      }
      this.savePrefs();
    },
    savePrefs() {
      let preferences = lodashCloneDeep(this.preferences);
      preferences.assets = preferences.assets || {};
      preferences.assets.enabled = this.assetsEnabled;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
      preferences = null;
    },
    updateCustomField(obj, prop, event) {
      if (!!event) {
        this.$set(obj, prop, event);
      }
    },
    handleItemSelected(selected) {
      if (selected && selected.item) {
        const assetsSelected = lodashCloneDeep(this.assetsSelected);
        if (selected.value) {
          assetsSelected.push(selected.item);
        } else {
          lodashRemove(assetsSelected, selected.item);
        }
        this.$store.dispatch(
          MODULE_PREFIX_ASSETS + GET_ASSETS_SELECTED,
          assetsSelected
        );
      }
    },
    handleToggleSelectAll(selected) {
      if (selected && selected.items) {
        const assetsSelected = selected.value ? selected.items : [];
        this.$store.dispatch(
          MODULE_PREFIX_ASSETS + GET_ASSETS_SELECTED,
          assetsSelected
        );
      }
    },
    handleClickAssetSelect() {
      const assetsSelected = [];
      this.assets.assets.forEach((asset) => {
        if (asset?.selected) {
          assetsSelected.push(asset);
        }
      });
      this.$store.dispatch(
        MODULE_PREFIX_ASSETS + GET_ASSETS_SELECTED,
        assetsSelected
      );
    },
    async handleClickAlert() {
      const notification = lodashCloneDeep(this.notification) || {};
      notification.formatted = notification.formatted || {};
      notification.formatted.docType = NOTIFICATION_ASSET_TYPE_VERIFIED_EVENT;
      notification.formatted.frequency = NOTIFICATION_FREQUENCY_IMMEDIATELY;
      notification.formatted.expiration = NOTIFICATION_EXPIRATION_ID_NEVER;
      notification.formatted.expirationDate = null;
      await this.$store.dispatch(
        MODULE_PREFIX_NOTIFICATIONS + GET_NOTIFICATION,
        notification
      );

      const modal = lodashCloneDeep(this.modal);
      modal.visible = true;
      modal.type = NOTIFICATION_ASSET_TYPE_VERIFIED_EVENT;
      this.$store.dispatch(MODULE_PREFIX_NOTIFICATIONS + GET_MODAL, modal);
    },
    async handleTableOptions(event) {
      const { sortBy, sortDesc, page, itemsPerPage } = event;
      const sortByField = sortBy[0];
      const isSortDesc = sortDesc[0];
      let sortDirection =
        isSortDesc === undefined
          ? sortOptions.NONE
          : isSortDesc
          ? sortOptions.DESCENDING
          : sortOptions.ASCENDING;
      this.options.page = page;
      if (this.pageNum !== page) {
        this.$store.dispatch(MODULE_PREFIX_ASSETS + GET_ASSETS_SELECTED, []);
      }
      this.options.itemsPerPage =
        itemsPerPage === -1 ? this.totalAssets : itemsPerPage;
      // Single sort at the moment
      switch (sortByField) {
        case headerOptionValues.COUNTRY_NAME:
          this.options.countrySortDirection = sortDirection;
          this.options.titleSortDirection = "";
          this.options.assetTypeSortDirection = "";
          break;
        case headerOptionValues.TITLE:
          this.options.titleSortDirection = sortDirection;
          this.options.countrySortDirection = "";
          this.options.assetTypeSortDirection = "";
          break;
        case headerOptionValues.ASSET_TYPE:
          this.options.assetTypeSortDirection = sortDirection;
          this.options.countrySortDirection = "";
          this.options.titleSortDirection = "";
          break;
        default:
          // Default to title asc
          this.options.countrySortDirection = "";
          this.options.titleSortDirection = sortOptions.ASCENDING;
          this.options.assetTypeSortDirection = "";
      }
      if (this.alertId) {
        this.options.alertIdSortDirection = sortOptions.DESCENDING;
        this.options.countrySortDirection = "";
        this.options.titleSortDirection = "";
        this.options.assetTypeSortDirection = "";
      }
      await this.$store.dispatch(
        MODULE_PREFIX_ASSETS + GET_ASSETS_TABLE_OPTIONS,
        this.options
      );
    },
    flyToItem(item) {
      this.map.flyTo({
        center: [item.longitude, item.latitude],
        zoom: 7,
      });
    },
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    "preferences.assets": {
      handler() {
        this.assetsEnabled = lodashGet(this.preferences, "assets.enabled");
      },
      immediate: true,
    },
    assetsSelected: {
      handler() {
        this.selected = lodashCloneDeep(this.assetsSelected);
      },
      deep: true,
      immediate: true,
    },
    options: {
      async handler(val) {
        await this.$store.dispatch(MODULE_PREFIX_ASSETS + GET_ASSETS, {
          options: val,
        });
        this.$emit(EMITTED_ACTIONS.CHANGE_FILTER, val);
      },
      deep: true,
    },
    filtersSelected: {
      handler() {
        if (this.filtersSelected?.filterTitle) {
          this.options.filterTitle = this.filtersSelected.filterTitle;
        }
        if (this.filtersSelected?.filterCountries) {
          this.options.filterCountries = this.filtersSelected.filterCountries;
        }
        if (this.filtersSelected?.filterAssetTypeIds) {
          this.options.filterAssetTypeIds = this.filtersSelected.filterAssetTypeIds;
        }
      },
      deep: true,
      immediate: true,
    },
    alertId: {
      handler() {
        if (this.alertId) {
          this.options.filterAlertId = this.alertId;
          this.options.alertIdSortDirection = sortOptions.DESCENDING;
        }
      },
      immediate: true,
    },
    assets: {
      handler(val) {
        if (val?.metadata) {
          this.pageNum = val.metadata.pageNumber + "";
          this.itemsPerPage = val.metadata.count;
          this.totalAssets = val.metadata.total;
        }
      },
    },
    notification: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal && !this.notification) {
          this.options = {
            filterTitle: null,
            filterCountries: [],
            filterAssetTypeIds: [],
            filterAlertId: null,
            titleSortDirection: sortOptions.NONE,
            countrySortDirection: sortOptions.NONE,
            assetTypeSortDirection: sortOptions.NONE,
            alertIdSortDirection: sortOptions.NONE,
          };
        }
      },
    },
  },
};
</script>

<style>
.assets-data-table .v-data-table-header__icon {
  position: relative;
  top: -2px;
  left: 7px;
}
.location-textfields .v-messages__message {
  color: #ff5252 !important;
}
</style>

<style scoped>
.hyp-table-container {
  width: 1200px;
  min-height: 500px;
}
.assets-data-table {
  overflow-y: auto;
}
.assets-modify-button {
  position: absolute;
}
.v-data-table {
  overflow-x: auto;
}
.asset-type-txt {
  position: relative;
  top: 6px;
}
</style>
